/**
 * Deleting undo material — the other half of writing it.
 *
 * Undo blobs are not harmless leftovers. A blob that outlives the work it
 * describes is a way to *resurrect* changes the user explicitly discarded: reopen
 * the order on another device, hit Undo, and the socket jumps back to a state that
 * was thrown away three steps ago. So every operation that discards work has to
 * discard its undo material too, on the server as well as locally.
 *
 * Three triggers:
 *
 *   undo            the tool (and anything applied after it) is no longer applied,
 *                   so its pre-apply socket is unreachable.
 *   backward revert `discardCheckpointsAfter` drops later steps — their blobs go
 *                   with them.
 *   post-export     everything collapses to one mesh per stage; all per-tool
 *                   material for every other step is deleted.
 *
 * Server deletes are fire-and-forget. A failed delete is not a correctness bug on
 * its own, because `undoManifestSync` gates hydration on workflow completion state
 * rather than on file presence — an orphaned blob is never adopted. It is still
 * storage we are paying for, so failures are logged loudly.
 */
import { loadBkDesignOrderContext } from '../../../shared/lib/bkDesignOrderContext';
import {
  deleteBkDesignUndoBlobs,
  isUndoApiUnavailable,
} from '../../../shared/services/erpApiService';
import { GLOBAL_STEP_ORDER } from '../../../shared/constants/flowConfig';
import {
  RETAINED_STEPS_AFTER_EXPORT,
  prunableStepsAfterExport,
} from '../../../shared/constants/designStages';
import {
  deleteBeforeMesh,
  deleteBeforeMeshesForSteps,
  retainBeforeMeshesForSteps,
  listLocalBeforeMeshes,
} from './beforeMeshCache';
import { invalidateUndoManifestCache } from './undoManifestSync';
import useUndoManifestStore from './undoManifestStore';

const LOG_PREFIX = '[UndoInvalidation]';

function currentOrder() {
  const ctx = loadBkDesignOrderContext();
  if (!ctx?.sales_order || !ctx?.customer) return null;
  return {
    orderKey: `${ctx.sales_order}:${ctx.customer}`,
    salesOrder: ctx.sales_order,
    customer: ctx.customer,
  };
}

/** Fire-and-forget server delete; resolves false rather than throwing. */
async function serverDelete(args, description) {
  const order = currentOrder();
  if (!order) return false;
  try {
    await deleteBkDesignUndoBlobs({
      salesOrderId: order.salesOrder,
      customer: order.customer,
      ...args,
    });
    invalidateUndoManifestCache();
    return true;
  } catch (e) {
    if (isUndoApiUnavailable(e)) return false;
    console.warn(`${LOG_PREFIX} server delete failed (${description})`, e);
    return false;
  }
}

/**
 * Drop the before-meshes for specific tools on one step.
 *
 * @param {string} stepId
 * @param {string[]} toolIds
 * @returns {Promise<void>}
 */
export async function deleteBeforeMeshesForTools(stepId, toolIds) {
  const ids = [...new Set((toolIds ?? []).filter(Boolean))];
  if (!stepId || ids.length === 0) return;

  const order = currentOrder();
  if (order) {
    await Promise.all(ids.map((toolId) => deleteBeforeMesh(order.orderKey, stepId, toolId)));
  }
  await serverDelete({ steps: [stepId], toolIds: ids }, `${stepId}/${ids.join(',')}`);
}

/**
 * Drop every scrap of undo material for the steps after `stepId`.
 *
 * Called from the backward-revert path, which has already discarded those steps'
 * meshes, session snapshots and operation logs.
 *
 * @param {string} stepId
 * @returns {Promise<void>}
 */
export async function invalidateUndoAfterStep(stepId) {
  const idx = GLOBAL_STEP_ORDER.indexOf(stepId);
  if (idx < 0) return;
  const laterSteps = GLOBAL_STEP_ORDER.slice(idx + 1);
  if (laterSteps.length === 0) return;

  useUndoManifestStore.getState().clearStepsAfter(stepId);

  const order = currentOrder();
  if (order) {
    const removed = await deleteBeforeMeshesForSteps(order.orderKey, laterSteps);
    if (removed) console.log(`${LOG_PREFIX} dropped ${removed} local blob(s) after ${stepId}`);
  }
  await serverDelete({ steps: laterSteps }, `steps after ${stepId}`);
}

/**
 * Drop the undo material for one step (used when a revert navigation leaves the
 * step it came from).
 *
 * @param {string} stepId
 */
export async function invalidateUndoForStep(stepId) {
  if (!stepId) return;
  useUndoManifestStore.getState().clearStep(stepId);

  const order = currentOrder();
  if (order) await deleteBeforeMeshesForSteps(order.orderKey, [stepId]);
  await serverDelete({ steps: [stepId] }, stepId);
}

/**
 * Post-export pruning: keep one mesh per design stage, delete everything else.
 *
 * Per-tool undo does not survive this, by design — an exported order is a
 * delivered artefact, and "go back" from here means going back a whole stage.
 * `resolvePrunedPreviousStep` is what navigation uses afterwards.
 *
 * @returns {Promise<{ localDeleted: number, serverDeleted: boolean, retained: string[] }>}
 */
export async function pruneUndoMaterialToStages() {
  const retained = [...RETAINED_STEPS_AFTER_EXPORT];
  const prunable = prunableStepsAfterExport();

  // Manifests for the retained steps stay: they still describe how each stage was
  // reached, which is worth keeping for support even without the blobs.
  useUndoManifestStore.getState().retainOnlySteps(retained);

  const order = currentOrder();
  let localDeleted = 0;
  if (order) {
    const before = listLocalBeforeMeshes(order.orderKey).length;
    localDeleted = await retainBeforeMeshesForSteps(order.orderKey, retained);
    console.log(
      `${LOG_PREFIX} post-export prune: ${localDeleted}/${before} local blob(s) removed, ` +
        `retaining ${retained.join(', ')}`,
    );
  }

  const serverDeleted = await serverDelete(
    { retainSteps: retained, steps: prunable },
    'post-export stage prune',
  );

  return { localDeleted, serverDeleted, retained };
}

/** Everything for the order — used when a design is abandoned or reset. */
export async function invalidateAllUndoMaterial() {
  useUndoManifestStore.getState().clearAll();
  const order = currentOrder();
  if (order) await retainBeforeMeshesForSteps(order.orderKey, []);
  await serverDelete({ retainSteps: [] }, 'whole order');
}
